/**
 * Authentication Service for CoinVAT API
 * Handles user authentication, registration, and session management
 */
import { apiClient } from './api-client';
import type {
  UserLogin,
  UserRegistration,
  TokenResponse,
  RefreshTokenRequest,
  PasswordChange,
  OAuthProvider,
} from './types';

export class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: UserLogin): Promise<TokenResponse> {
    const response = await apiClient.post<TokenResponse>('/api/v1/auth/login', credentials, {
      useAuth: false,
    });
    
    apiClient.storeTokens(response);
    return response;
  }

  /**
   * Register new user account
   */
  async register(userData: UserRegistration): Promise<TokenResponse> {
    const response = await apiClient.post<TokenResponse>('/api/v1/auth/register', userData, {
      useAuth: false,
    });
    
    apiClient.storeTokens(response);
    return response;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<TokenResponse> {
    const refreshTokenValue = this.getStoredRefreshToken();
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    const request: RefreshTokenRequest = { refresh_token: refreshTokenValue };
    const response = await apiClient.post<TokenResponse>('/api/v1/auth/refresh', request, {
      useAuth: false,
    });
    
    apiClient.storeTokens(response);
    return response;
  }

  /**
   * Logout user and revoke refresh token
   */
  async logout(): Promise<void> {
    const refreshTokenValue = this.getStoredRefreshToken();
    if (refreshTokenValue) {
      try {
        const request: RefreshTokenRequest = { refresh_token: refreshTokenValue };
        await apiClient.post('/api/v1/auth/logout', request);
      } catch (error) {
        console.warn('Logout request failed:', error);
        // Continue with local logout even if API call fails
      }
    }
    
    apiClient.clearTokens();
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: PasswordChange): Promise<void> {
    await apiClient.post('/api/v1/auth/change-password', passwordData);
  }

  /**
   * Get OAuth login URL for external provider
   */
  getOAuthUrl(provider: OAuthProvider): string {
    return `${apiClient['baseURL']}/api/v1/auth/oauth/${provider}`;
  }

  /**
   * Handle OAuth callback (this would typically be handled by the backend)
   */
  async handleOAuthCallback(provider: OAuthProvider, code: string, state: string): Promise<TokenResponse> {
    const response = await apiClient.get<TokenResponse>(
      `/api/v1/auth/oauth/${provider}/callback?code=${code}&state=${state}`,
      { useAuth: false }
    );
    
    apiClient.storeTokens(response);
    return response;
  }

  // Utility methods
  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('coinvat_refresh_token');
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  /**
   * Ensure the current token is valid, refresh if necessary
   */
  async ensureValidToken(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      // Try to refresh token if needed
      if (this.isTokenExpired()) {
        await this.refreshToken();
      }
      return true;
    } catch (error) {
      apiClient.clearTokens();
      return false;
    }
  }

  private isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('coinvat_token_expires_at');
    if (!expiresAt) return true;
    
    // Add buffer time to refresh token before it expires
    const bufferTime = 60 * 1000; // 1 minute in milliseconds
    return Date.now() > (parseInt(expiresAt) - bufferTime);
  }
}

// Export singleton instance
export const authService = new AuthService();